import { mediaQueriesBP } from "@hardware/Utilities/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

export const enum fields {
    backgroundImage = "backgroundImage",
    backgroundImageMobile = "backgroundImageMobile",
    imageAltText = "imageAltText",
    backgroundColor = "backgroundColor",
    cardBackgroundColor = "cardBackgroundColor",
    title = "title",
    content = "content",
    formTitle = "formTitle",
    displayBackgroundImage = "Background image",
    displayBackgroundImageMobile = "Background image (mobile)",
    displayImageAltText = "Image alt text",
    displayBackgroundColor = "Overlay color",
    displayCardBackgroundColor = "Card background color",
    displayTitle = "Title",
    displayContent = "Content",
    displayFormTitle = "Form Title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundImage]: string;
        [fields.backgroundImageMobile]: string;
        [fields.imageAltText]: string;
        [fields.backgroundColor]: string;
        [fields.cardBackgroundColor]: string;
        [fields.title]: string;
        [fields.content]: string;
        [fields.formTitle]: string;
    };
}

export type Props = OwnProps & HasShellContext;

export interface CampaingTwoColumnFormContainerStyles {
    wrapper?: InjectableCss;
    inner?: InjectableCss;
    leftColumn?: InjectableCss;
    rightColumn?: InjectableCss;
    title?: InjectableCss;
    content?: InjectableCss;
    card?: InjectableCss;
    cardTitle?: TypographyPresentationProps;
    formZoneWrapper?: InjectableCss;
    formPlaceholder?: InjectableCss;
}

export const campaingTwoColumnFormContainerStyles: CampaingTwoColumnFormContainerStyles = {
    wrapper: {
        css: css`
            position: relative;
            width: 100vw;
            left: 50%;
            right: 50%;
            margin-left: -50vw;
            margin-right: -50vw;
            padding: 0;
            background-repeat: no-repeat;
            background-position: center;
            background-size: cover;
            min-height: 400px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            
            ${mediaQueriesBP.minMD} {
                flex-direction: row;
                min-height: 600px;
            }
        `,
    },
    inner: {
        css: css`
            box-sizing: border-box;
            max-width: 1440px;
            margin: 0 auto;
            width: 100%;
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 40px;
            padding: 80px 120px;
            align-self: stretch;

            ${mediaQueriesBP.maxLG} {
                flex-direction: column;
                gap: 24px;
                padding: 48px 24px;
            }

            ${mediaQueriesBP.maxMD} {
                padding: 40px 20px;
            }
        `,
    },
    leftColumn: {
        css: css`
            flex: 1 1 0;
            min-width: 0;
            height: 100%;
            
            ${mediaQueriesBP.minMD} {
                flex: 1 1 auto;
            }
        `,
    },
    rightColumn: {
        css: css`
            flex: 0 0 auto;
            width: 100%;
            height: 100%;
            max-width: 100%;
            
            ${mediaQueriesBP.minMD} {
                flex: 1 1 auto;
            }
                
            ${mediaQueriesBP.minLG} {
                max-width: 50%;
            }
        `,
    },


    card: {
        css: css`
            width: 100%;
            padding: 24px;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
            height: 100%;
        `,
    },
    cardTitle: {
        variant: "h3",
        css: css`
            margin: 0 0 14px;
        `,
    },
    formZoneWrapper: {
        css: css`
            min-height: 260px;
        `,
    },
    formPlaceholder: {
        css: css`
            margin-top: 12px;
            padding: 14px;
            border: 1px dashed rgba(0, 0, 0, 0.25);
            background: rgba(0, 0, 0, 0.03);
            font-size: 14px;
        `,
    },
};

export const styles = campaingTwoColumnFormContainerStyles;

const hexToRgba = (hex: string, alpha: number) => {
    const normalized = (hex || "").trim().replace("#", "");
    const resolved =
        normalized.length === 3
            ? `${normalized[0]}${normalized[0]}${normalized[1]}${normalized[1]}${normalized[2]}${normalized[2]}`
            : normalized;

    if (resolved.length !== 6) {
        return hex;
    }

    const r = parseInt(resolved.substring(0, 2), 16);
    const g = parseInt(resolved.substring(2, 4), 16);
    const b = parseInt(resolved.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const CampaingTwoColumnFormContainer = ({
    id,
    fields: {
        backgroundImage,
        backgroundImageMobile,
        imageAltText,
        backgroundColor,
        cardBackgroundColor,
        title,
        content,
        formTitle,
    },
    shellContext: { isEditing },
}: Props) => {
    const wrapperStyle = styles.wrapper ?? {};
    const overlayColor = backgroundColor || "#224D64";
    const overlayStrong = hexToRgba(overlayColor, 0.92);
    const overlayMid = hexToRgba(overlayColor, 0.7);
    const overlayNone = hexToRgba(overlayColor, 0);

    const mobileImage = backgroundImageMobile || backgroundImage;
    const mobileBgImage =
        mobileImage && `linear-gradient(90deg, ${overlayStrong} 0%, ${overlayMid} 55%, ${overlayNone} 100%), url("${mobileImage}")`;
    const desktopBgImage =
        backgroundImage &&
        `linear-gradient(90deg, ${overlayStrong} 0%, ${overlayMid} 55%, ${overlayNone} 100%), url("${backgroundImage}")`;

    wrapperStyle.css = css`
        ${wrapperStyle?.css}
        background-color: ${overlayColor};
        ${mobileBgImage ? `background-image: ${mobileBgImage};` : ""}
        color: #fff;

        ${desktopBgImage &&
        css`
            ${mediaQueriesBP.minMD} {
                background-image: ${desktopBgImage};
            }
        `}
    `;

    const cardStyle = styles.card ?? {};
    cardStyle.css = css`
        ${cardStyle?.css}
        background-color: ${cardBackgroundColor};
        color: #000;
    `;

    const cardTitleStyle: TypographyPresentationProps = {
        ...styles.cardTitle,
        color: "#000",
    };

    return (
        <StyledWrapper {...wrapperStyle} aria-label={imageAltText || undefined}>
            <StyledWrapper {...styles.inner}>
                <StyledWrapper {...styles.leftColumn}>
                    {title && (
                        <StyledWrapper {...styles.title}>
                            {parse(transformContentHtml(title), parserOptions)}
                        </StyledWrapper>
                    )}
                    {content && (
                        <StyledWrapper {...styles.content}>
                            {parse(transformContentHtml(content), parserOptions)}
                        </StyledWrapper>
                    )}
                </StyledWrapper>

                <StyledWrapper {...styles.rightColumn}>
                    <StyledWrapper {...cardStyle}>
                        {formTitle && <Typography {...cardTitleStyle}>{formTitle}</Typography>}

                        <StyledWrapper {...styles.formZoneWrapper}>
                            <Zone contentId={id} zoneName="Form" />
                        </StyledWrapper>

                        {isEditing && (
                            <StyledWrapper {...styles.formPlaceholder}>
                                Add your form widgets inside the <strong>Form</strong> zone (this widget is only the
                                2-column container).
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withIsInShell(CampaingTwoColumnFormContainer) as any,
    definition: {
        displayName: "Campaign 2-Column Form Container",
        group: "Campaign" as any,
        icon: "table-columns",
        fieldDefinitions: [
            {
                name: fields.backgroundImage,
                displayName: fields.displayBackgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.backgroundImageMobile,
                displayName: fields.displayBackgroundImageMobile,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.imageAltText,
                displayName: fields.displayImageAltText,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.backgroundColor,
                displayName: fields.displayBackgroundColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "#224D64",
                fieldType: "General",
            },
            {
                name: fields.cardBackgroundColor,
                displayName: fields.displayCardBackgroundColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "#ffffff",
                fieldType: "General",
            },
            {
                name: fields.title,
                displayName: fields.displayTitle,
                editorTemplate: "RichTextField",
                defaultValue: "<h2>Ready to Transform Your Inventory Management?</h2>",
                fieldType: "Translatable",
            },
            {
                name: fields.content,
                displayName: fields.displayContent,
                editorTemplate: "RichTextField",
                defaultValue:
                    "<p>Join the growing number of businesses saving time and money with our vendor-managed inventory portal.</p><ul><li>No setup fees or long-term contracts</li><li>Free training and ongoing support</li><li>ROI typically seen within 90 days</li></ul><p>Have questions? Contact us or call us directly at (800) 645-0736.</p>",
                fieldType: "Translatable",
            },
            {
                name: fields.formTitle,
                displayName: fields.displayFormTitle,
                editorTemplate: "TextField",
                defaultValue: "Get Started Today",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;

